import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Plus, MapPin, CalendarDays, Luggage, ChevronRight,
  Sparkles, BookOpen, Star, Share2, PenLine, Compass, MoreHorizontal,
  Shield,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SectionRibbon } from "@/components/AppShell";
import UploadPhotoButton from "@/components/UploadPhotoButton";
import PhotoCarousel from "@/components/PhotoCarousel";
import RowMenu from "@/components/RowMenu";
import MoveDialog from "@/components/MoveDialog";
import ShareChapterDialog from "@/components/ShareChapterDialog";
import BackpackList from "@/components/BackpackList";
import MissingCoverPlaceholder from "@/components/MissingCoverPlaceholder";
// Phase 5 (2026-07-28): GooglePlacesPhoto retired.
import { NewTripDialog } from "@/pages/BookedTripsPage";
import { HighlightIconGlyph } from "@/lib/highlightIcons";
import { toast } from "sonner";
import { Check, X as XIcon } from "lucide-react";

const CURRENT_YEAR = new Date().getFullYear();

const TIDS = {
  page: "chapter-year-page",
  newStoryBtn: "chapter-year-new-story-btn",
  shareBtn: "chapter-year-share-btn",
  story: (id) => `chapter-year-story-${id}`,
  storyMenu: (id) => `chapter-story-menu-${id}`,
  trip: (id) => `chapter-year-trip-${id}`,
  tripMenu: (id) => `chapter-trip-menu-${id}`,
};

const inp =
  "w-full px-3 py-2 rounded-lg bg-tb-cream-2 border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm";

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Story dialog handles both "create" (no `initial` prop) and "edit"
 * (initial = existing story). On submit it POSTs or PUTs accordingly.
 */
function StoryDialog({ year, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => ({
    trip_name: initial?.trip_name || "",
    country: initial?.country || "",
    start_date: initial?.start_date || "",
    end_date: initial?.end_date || "",
    favorite_moments: (initial?.favorite_moments?.length ? [...initial.favorite_moments, ""] : [""]),
    icon: initial?.icon || "compass",
    stamp_color: initial?.stamp_color || "#1a3a5f",
    photos: initial?.photos || [],
  }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setHi = (idx, v) => setForm((f) => {
    const next = [...f.favorite_moments];
    next[idx] = v;
    if (idx === next.length - 1 && v) next.push("");
    return { ...f, favorite_moments: next };
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date) return toast.error("Add a start date");
    const payload = {
      ...form,
      year,
      image: form.photos?.[0] || initial?.image || "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80",
      code: initial?.code || "",
      rating: initial?.rating ?? 5,
      favorite_moments: form.favorite_moments.filter(Boolean),
      photos: form.photos || [],
    };
    try {
      if (isEdit) {
        await api.put(`/chapters/${initial.id}`, payload);
        toast.success("Story updated ✦");
      } else {
        await api.post("/chapters", payload);
        toast.success("Story added to your chapter ✦");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3 overflow-y-auto">
      <form onSubmit={submit} role="dialog" aria-modal="true"
        className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-md w-full max-h-[92vh] overflow-y-auto">
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span className="font-hand text-xl">
            {isEdit ? `edit story · ${year}` : `log a new story · ${year}`}
          </span>
          <button type="button" onClick={onClose} className="font-hand text-tb-gold tb-tap">close</button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="font-hand text-tb-navy/65 text-sm">Location</span>
            <input required value={form.trip_name} onChange={(e) => set("trip_name", e.target.value)}
              placeholder="Lisbon" className={inp} />
          </label>
          <label className="block">
            <span className="font-hand text-tb-navy/65 text-sm">Country</span>
            <input required value={form.country} onChange={(e) => set("country", e.target.value)}
              placeholder="Portugal" className={inp} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="font-hand text-tb-navy/65 text-sm">Start</span>
              <input required type="date" value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)} className={inp} />
            </label>
            <label className="block">
              <span className="font-hand text-tb-navy/65 text-sm">End</span>
              <input required type="date" value={form.end_date}
                onChange={(e) => set("end_date", e.target.value)} className={inp} />
            </label>
          </div>
          <div>
            <span className="font-hand text-tb-navy/65 text-sm">Highlights</span>
            {form.favorite_moments.map((m, i) => (
              <input key={i} value={m} onChange={(e) => setHi(i, e.target.value)}
                placeholder={i === 0 ? "the morning swim · the espresso · …" : "another highlight"}
                className={inp + " mt-1"} />
            ))}
          </div>
          <div>
            <span className="font-hand text-tb-navy/65 text-sm">Photo album (up to 10)</span>
            <div className="mt-1">
              <UploadPhotoButton
                testId="story-photos-input"
                value={form.photos}
                onChange={(v) => set("photos", v)}
                max={10}
                overflowMessage="More storage options coming soon!"
                label={form.photos?.length ? `Add more (${form.photos.length}/10)` : "Add photos (up to 10)"}
              />
            </div>
          </div>
          <button className="w-full py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap">
            {isEdit ? "save changes" : `add story to chapter ${year}`}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Lightweight inline edit dialog for booked trips — only the fields that
 * actually live on the trip card. Full trip editing remains on the trip detail page.
 */
function TripEditDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial.name || "",
    destination_name: initial.destination_name || "",
    country: initial.country || "",
    start_date: initial.start_date || "",
    end_date: initial.end_date || "",
    total_budget: String(initial.total_budget || ""),
    notes: initial.notes || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/booked-trips/${initial.id}`, {
        ...form,
        total_budget: parseFloat(form.total_budget) || 0,
        trip_type: initial.trip_type || "pleasure",
        destination_id: initial.destination_id || null,
        image: initial.image || "",
      });
      toast.success("Trip updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3">
      <form onSubmit={submit} className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-md w-full">
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span className="font-hand text-xl">edit trip</span>
          <button type="button" onClick={onClose} className="font-hand text-tb-gold tb-tap">close</button>
        </div>
        <div className="p-4 space-y-3">
          <input className={inp} placeholder="Trip name" value={form.name}
            onChange={(e) => set("name", e.target.value)} required />
          <div className="grid grid-cols-2 gap-2">
            <input className={inp} placeholder="Destination" value={form.destination_name}
              onChange={(e) => set("destination_name", e.target.value)} />
            <input className={inp} placeholder="Country" value={form.country}
              onChange={(e) => set("country", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="font-hand text-tb-navy/65 text-xs ml-1">start</span>
              <input type="date" className={inp} value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)} />
            </label>
            <label className="block">
              <span className="font-hand text-tb-navy/65 text-xs ml-1">end</span>
              <input type="date" className={inp} value={form.end_date}
                onChange={(e) => set("end_date", e.target.value)} />
            </label>
          </div>
          <label className="block">
            <span className="font-hand text-tb-navy/65 text-xs ml-1">total budget (USD)</span>
            <input className={inp} inputMode="decimal" value={form.total_budget}
              onChange={(e) => set("total_budget", e.target.value.replace(/[^\d.]/g, ""))} />
          </label>
          <button className="w-full py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap">
            save changes
          </button>
        </div>
      </form>
    </div>
  );
}

export default function ChapterYearPage() {
  const { year } = useParams();
  const yearN = parseInt(year, 10);
  const nav = useNavigate();
  const [data, setData] = useState({ stories: [], booked_trips: [] });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  // Phase 2 merge: also let users create a booked trip inline from
  // the chapter footer — the Booked Trips page is deprecated as a
  // standalone destination.
  const [showNewTrip, setShowNewTrip] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [editingTrip, setEditingTrip] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null); // {kind, item}
  const [showShare, setShowShare] = useState(false);
  // Item #38 — story detail modal target. Null = closed.
  const [openStory, setOpenStory] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/chapter-folders/${yearN}`);
      setData(data);
    } catch (err) {
      console.error("[ChapterYear] load failed:", err);
      toast.error("Couldn't load chapter");
      nav("/chapters");
    } finally { setLoading(false); }
  }, [yearN, nav]);

  useEffect(() => { load(); }, [load]);

  // 2026-07-17 Fix 6 — refetch the chapter timeline whenever another
  // page fires `travelbio:chapter-changed` (story/trip delete, move,
  // photo/highlight add, etc). Prevents ghost cards after returning
  // from the Detail page where the record was just removed.
  useEffect(() => {
    const onChanged = () => { load(); };
    window.addEventListener("travelbio:chapter-changed", onChanged);
    return () => window.removeEventListener("travelbio:chapter-changed", onChanged);
  }, [load]);

  const removeStory = async (s) => {
    if (!window.confirm(`Delete the story "${s.trip_name}"? This can't be undone.`)) return;
    try { await api.delete(`/chapters/${s.id}`); toast.success("Story deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeTrip = async (t) => {
    if (!window.confirm(`Delete the trip "${t.name}"? This can't be undone.`)) return;
    try { await api.delete(`/booked-trips/${t.id}`); toast.success("Trip deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const moveStory = async (payload) => {
    try {
      await api.patch(`/chapters/${moveTarget.item.id}/move`, payload);
      toast.success(`Moved to chapter ${payload.year}`);
      setMoveTarget(null);
      if (payload.year !== yearN) nav(`/chapters/${payload.year}`);
      else load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't move");
    }
  };

  const moveTrip = async (payload) => {
    try {
      await api.patch(`/booked-trips/${moveTarget.item.id}/move`, payload);
      const target = payload.trip_type === "business"
        ? "business"
        : `chapter ${payload.year}`;
      toast.success(`Moved to ${target}`);
      setMoveTarget(null);
      if (payload.trip_type === "business") nav("/chapters/business");
      else if (payload.year !== yearN) nav(`/chapters/${payload.year}`);
      else load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't move");
    }
  };

  // Item #36 dedup preserved.
  const promotedTripIds = new Set(
    (data.stories || [])
      .map((s) => s.auto_promoted_from_trip_id)
      .filter(Boolean)
  );
  const visibleBookedTrips = (data.booked_trips || []).filter(
    (t) => !promotedTripIds.has(t.id)
  );

  const storyCount = (data.stories || []).length;
  // Adventures mirror the story count until the schema grows a
  // distinct concept (see Phase 2). Booked trips that dedup down to
  // a promoted story don't count twice.
  const adventureCount = storyCount + visibleBookedTrips.length;

  const isEmpty = storyCount === 0 && visibleBookedTrips.length === 0;

  return (
    <div
      data-testid={TIDS.page}
      // Full-viewport dark navy shell — the "outside of the book".
      // Uses the same token as every other dark-navy surface across
      // the app (AppShell drawer, primary buttons) so we don't
      // introduce a new palette entry.
      className="min-h-[100vh] bg-white w-full"
    >
      {/* Book page: cream rectangle with page-depth decoration behind
          it (Phase 2 polish item 1). The wrapper carries the layered
          "stacked pages" strips (right fore-edge + bottom); the inner
          div is the actual reading surface with the shadow ring. */}
      <div className="relative mx-auto my-6 md:my-10 max-w-[880px] pr-16 md:pr-20">
        {/* Right fore-edge fan — 4 thin strips receding outward.
            2026-07-17: with the book page now WHITE (was cream), we
            switched the strips from `bg-tb-cream-*` to slate greys so
            the page-depth still reads visually against the white
            outer viewport. Same 3px width + progressive fade.
            Sits BEHIND the year rail (z-0) so tabs still protrude
            visually from the fore-edge. */}
        <div aria-hidden="true" className="absolute inset-y-1 -right-0 pr-14 md:pr-16 flex justify-end pointer-events-none z-0">
          <span className="w-1 h-full bg-slate-100 rounded-r translate-x-[3px]" />
          <span className="w-1 h-full bg-slate-200 rounded-r translate-x-[6px] scale-y-[0.985]" />
          <span className="w-1 h-full bg-slate-300/80 rounded-r translate-x-[9px] scale-y-[0.97]" />
          <span className="w-1 h-full bg-slate-300/60 rounded-r translate-x-[12px] scale-y-[0.955]" />
        </div>
        {/* Bottom fanned edge — 3 thin horizontal strips just below the
            page bottom to give depth from below too. */}
        <div aria-hidden="true" className="absolute -bottom-1 left-3 right-16 md:right-20 flex flex-col pointer-events-none z-0">
          <span className="h-1 bg-slate-100 rounded-b -mx-1 translate-y-[1px]" />
          <span className="h-1 bg-slate-200 rounded-b mx-1 translate-y-[2px] scale-x-[0.99]" />
          <span className="h-1 bg-slate-300/70 rounded-b mx-2 translate-y-[3px] scale-x-[0.98]" />
        </div>

        <div
          data-testid="chapter-book-page"
          className="relative z-10 rounded-2xl bg-white border border-tb-navy/12 shadow-[0_20px_50px_-16px_rgba(26,58,95,0.35)] px-5 md:px-10 pt-10 pb-4"
        >
        {/* Top-left bookmark ribbon — navy tab hanging off the corner
            with a gold compass-rose glyph. Anchored so it protrudes
            ~8px above the page top; z-20 keeps it above the page shadow
            and any content that touches the top edge. */}
        <div
          aria-hidden="true"
          className="absolute -top-2 left-6 md:left-10 w-8 md:w-10 h-16 md:h-20 bg-tb-navy-deep flex items-start justify-center pt-2 shadow-md z-20"
          style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 82%, 0 100%)" }}
        >
          <CompassRoseSvg className="w-4 h-4 md:w-5 md:h-5 text-tb-gold-bright" />
        </div>

        {/* Phase 2 merge B3: pending-confirmation pills. Rendered at the
            very top of the book page (above the title) so the user
            sees them the moment they land on the chapter. Uses the
            existing awaiting_confirmation trip rows — the surface just
            moved from the deprecated Booked Trips page. */}
        <PendingConfirmationBar onChange={load} />

        {/* Center-top title block */}
        <div className="text-center pt-4 pb-2">
          <h1
            className="font-serif-it italic text-tb-navy text-4xl sm:text-5xl lg:text-6xl leading-tight"
            data-testid="chapter-book-title"
          >
            Chapter {yearN}
          </h1>
          <CompassDivider />
          <p className="mt-1 font-hand text-tb-navy/60 text-sm">
            {storyCount} {storyCount === 1 ? "Story" : "Stories"} <span className="text-tb-gold-bright">•</span> {adventureCount} {adventureCount === 1 ? "Adventure" : "Adventures"}
          </p>
        </div>

        {/* Body — story timeline OR empty state */}
        <div className="mt-6 md:mt-8">
          {loading ? (
            <div className="space-y-4">
              {[0, 1].map((i) => (
                <div key={i} className="h-24 rounded-2xl bg-white/70 animate-pulse" />
              ))}
            </div>
          ) : isEmpty ? (
            <EmptyChapterState
              year={yearN}
              onAddPast={() => setShowNew(true)}
              onAddBookedTrip={() => setShowNewTrip(true)}
            />
          ) : (
            <StoryTimeline
              stories={data.stories}
              bookedTrips={visibleBookedTrips}
              onOpenStory={(s) => nav(`/chapters/${yearN}/trip/${s.id}`)}
              onEditStory={(s) => nav(`/chapters/${yearN}/trip/${s.id}?edit=1`)}
              onDeleteStory={removeStory}
              onMoveStory={(s) => setMoveTarget({ kind: "story", item: s })}
              onOpenTrip={(t) => {
                const y = parseInt((t.start_date || "").slice(0, 4), 10) || yearN;
                nav(`/chapters/${y}/trip/${t.id}`);
              }}
              onEditTrip={(t) => {
                const y = parseInt((t.start_date || "").slice(0, 4), 10) || yearN;
                nav(`/chapters/${y}/trip/${t.id}?edit=1`);
              }}
              onDeleteTrip={removeTrip}
              onMoveTrip={(t) => setMoveTarget({ kind: "trip", item: t })}
            />
          )}
        </div>

        {/* Bottom "Write a new story" footer — Phase 1 polish A3:
            two buttons (booked trip + past story). 2026-07-17: copy +
            visibility are now year-context-aware — see WriteNewStoryFooter. */}
        <WriteNewStoryFooter
          year={yearN}
          onAddBookedTrip={() => setShowNewTrip(true)}
          onAddPastStory={() => setShowNew(true)}
        />

        {/* Share button — small, at the very bottom next to the CTA.
            Preserves the Phase 0 flow but stays out of the hero title
            area (per spec: share is not part of the redesigned header). */}
        {storyCount > 0 && (
          <div className="mt-2 flex justify-center">
            <button
              data-testid={TIDS.shareBtn}
              onClick={() => setShowShare(true)}
              className="inline-flex items-center gap-1.5 font-hand text-xs text-tb-navy/50 hover:text-tb-coral tb-tap"
            >
              <Share2 className="w-3 h-3" /> share this chapter
            </button>
          </div>
        )}
        </div>{/* end .z-10 cream reading surface */}
      </div>{/* end book-page wrapper (fanned edges) */}

      {/* Right-rail year tabs — sticky-ish to the right edge of the
          viewport. Buffer between rail and viewport edge lives on the
          rail itself. */}
      <YearRail
        selectedYear={yearN}
        onSelect={(y) => nav(`/chapters/${y}`)}
      />

      {/* All dialogs preserved from Phase 0 — the redesign is layout-
          only, none of the underlying flows change. */}
      {showNew && (
        <StoryDialog
          year={yearN}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }}
        />
      )}
      {showNewTrip && (
        <NewTripDialog
          onClose={() => setShowNewTrip(false)}
          onCreated={(_t) => {
            // Phase 2 merge: after creating a trip we reload the
            // timeline; the trip's start_date determines whether it
            // renders in the current year or somewhere else. We do
            // NOT navigate away — user stays on the current chapter.
            setShowNewTrip(false);
            load();
          }}
        />
      )}
      {editingStory && (
        <StoryDialog
          year={yearN}
          initial={editingStory}
          onClose={() => setEditingStory(null)}
          onSaved={() => { setEditingStory(null); load(); }}
        />
      )}
      {editingTrip && (
        <TripEditDialog
          initial={editingTrip}
          onClose={() => setEditingTrip(null)}
          onSaved={() => { setEditingTrip(null); load(); }}
        />
      )}
      {/* NOTE: 2026-07-17 — Chapter card ⋯ → Edit now navigates to the
          unified detail page at `/chapters/:year/trip/:id?edit=1`, so
          the legacy `editingStory` / `editingTrip` state paths above
          are dead. The mounts + `StoryDialog` / `TripEditDialog`
          components are kept in place for backwards compatibility in
          case any residual caller still `setEditingStory(...)`s them. */}
      {moveTarget && (
        <MoveDialog
          kind={moveTarget.kind}
          current={{
            year: moveTarget.kind === "story"
              ? moveTarget.item.year
              : parseInt((moveTarget.item.start_date || "").slice(0, 4), 10) || yearN,
            trip_type: moveTarget.item.trip_type || "pleasure",
          }}
          onClose={() => setMoveTarget(null)}
          onConfirm={moveTarget.kind === "story" ? moveStory : moveTrip}
        />
      )}
      {showShare && (
        <ShareChapterDialog
          year={yearN}
          stories={data.stories}
          onClose={() => setShowShare(false)}
        />
      )}
      {openStory && (
        <StoryDetailModal
          story={openStory}
          onClose={() => setOpenStory(null)}
        />
      )}
    </div>
  );
}


// =============================================================
//  Phase 1 book-redesign helpers
// =============================================================

/**
 * Small gold compass-rose glyph inlined as SVG so we don't ship a
 * new asset and the color inherits from the surrounding text color.
 * The four-point rose is deliberately understated — a full 8-point
 * would compete with the serif titles.
 */
function CompassRoseSvg({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
    </svg>
  );
}

/**
 * Thin horizontal line with a compass rose in the middle, printed
 * under the "Chapter {year}" title. A single flexbox row with two
 * flex-1 hairlines keeps the divider centred regardless of viewport
 * width — no absolute positioning.
 */
function CompassDivider() {
  return (
    <div
      aria-hidden="true"
      data-testid="chapter-compass-divider"
      className="mt-2 flex items-center justify-center gap-2 px-6 md:px-16 opacity-80"
    >
      <span className="flex-1 h-px bg-gradient-to-r from-transparent to-tb-gold" />
      <CompassRoseSvg className="w-4 h-4 text-tb-gold-bright" />
      <span className="flex-1 h-px bg-gradient-to-l from-transparent to-tb-gold" />
    </div>
  );
}

/**
 * "Write a new story" footer strip at the bottom of the book page.
 * Pinned inside the cream rectangle so it always sits below the
 * timeline — not stuck to the viewport.
 *
 * 2026-07-17: copy + button set are now year-context-aware.
 *   • past year        → primary `Add a past story`, secondary `Add a booked trip`
 *   • current year     → both buttons equally weighted
 *   • future year      → only `Add a booked trip` (past-story doesn't apply)
 */
function WriteNewStoryFooter({ year, onAddBookedTrip, onAddPastStory }) {
  const currentYear = new Date().getFullYear();
  const isPast = year < currentYear;
  const isCurrent = year === currentYear;
  const isFuture = year > currentYear;

  // Whisper line under the pen glyph — hints at the year context.
  const whisper = isFuture
    ? "Plan the trips that will become tomorrow's stories."
    : "Capture the moment. Cherish it forever.";

  // Booked-trip button — always visible, but its visual weight flips
  // depending on year context (primary for future/current, secondary
  // for past where the primary is the past-story CTA).
  const bookedIsPrimary = !isPast; // future + current → primary
  const bookedClass = bookedIsPrimary
    ? "inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-tb-navy-deep text-white border-2 border-tb-navy-deep font-hand text-sm tb-tap hover:brightness-110 shadow-sm"
    : "inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-tb-navy border-2 border-tb-navy font-hand text-sm tb-tap hover:bg-tb-navy hover:text-white transition-colors";

  // Past-story button — primary for past, equally-weighted for current,
  // and HIDDEN entirely for future years.
  const showPastStory = !isFuture;
  const pastStoryIsPrimary = isPast;
  const pastStoryClass = pastStoryIsPrimary
    ? "inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-tb-navy-deep text-white border-2 border-tb-navy-deep font-hand text-sm tb-tap hover:brightness-110 shadow-sm"
    : "inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-tb-navy border-2 border-tb-navy font-hand text-sm tb-tap hover:bg-tb-navy hover:text-white transition-colors";

  return (
    <div
      data-testid="chapter-write-new-footer"
      data-year-context={isPast ? "past" : isCurrent ? "current" : "future"}
      className="mt-8 pt-4 border-t border-dashed border-tb-navy/15"
    >
      <div className="flex items-center gap-2 mb-2">
        <PenLine className="w-4 h-4 text-tb-gold-bright shrink-0" />
        <div className="font-hand text-tb-navy/60 text-xs md:text-sm italic">
          {whisper}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="chapter-footer-add-booked-trip"
          onClick={onAddBookedTrip}
          className={bookedClass}
        >
          <Luggage className="w-4 h-4" /> Add a booked trip
        </button>
        {showPastStory && (
          <button
            type="button"
            data-testid="chapter-footer-add-past-story"
            onClick={onAddPastStory}
            className={pastStoryClass}
          >
            <PenLine className="w-4 h-4" /> Add a past story
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Vertical timeline of story cards for this year. Phase 2 merge B2:
 * items are now grouped into three sections rendered in this order:
 *
 *   1. Happening Now  (today between start_date and end_date)
 *   2. Upcoming       (start_date > today)
 *   3. Past           (everything else, incl. auto-promoted stories)
 *
 * A group's divider header is only rendered when the group has at
 * least one item. Within each group items sort by start_date ASC.
 */
function StoryTimeline({
  stories, bookedTrips,
  onOpenStory, onEditStory, onDeleteStory, onMoveStory,
  onOpenTrip, onEditTrip, onDeleteTrip, onMoveTrip,
}) {
  const now = new Date();
  const todayISO = now.toISOString().slice(0, 10);
  const inRange = (start, end) => {
    if (!start) return false;
    const s = String(start).slice(0, 10);
    const e = end ? String(end).slice(0, 10) : s;
    return s <= todayISO && todayISO <= e;
  };
  const isFuture = (start) => start && String(start).slice(0, 10) > todayISO;

  // Stories = always PAST (auto-promoted or user-authored retrospective).
  const storyRows = stories.map((s) => ({ kind: "story", item: s, sortKey: s.start_date || "" }));
  // Trips split into three buckets by start_date.
  const happeningNow = [];
  const upcoming = [];
  const pastTrips = [];
  for (const t of bookedTrips) {
    const row = { kind: "trip", item: t, sortKey: t.start_date || "" };
    if (inRange(t.start_date, t.end_date)) happeningNow.push(row);
    else if (isFuture(t.start_date)) upcoming.push(row);
    else pastTrips.push(row);
  }
  const past = [...storyRows, ...pastTrips].sort((a, b) =>
    a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0
  );
  happeningNow.sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1));
  upcoming.sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1));

  const renderCard = (r, opts) => {
    if (r.kind === "story") {
      return (
        <TimelineStoryCard
          s={r.item}
          onOpen={() => onOpenStory(r.item)}
          onEdit={() => onEditStory(r.item)}
          onDelete={() => onDeleteStory(r.item)}
          onMove={() => onMoveStory(r.item)}
        />
      );
    }
    return (
      <TimelineBookedTripCard
        t={r.item}
        variant={opts?.variant || "past"}
        onOpen={() => onOpenTrip(r.item)}
        onEdit={() => onEditTrip(r.item)}
        onDelete={() => onDeleteTrip(r.item)}
        onMove={() => onMoveTrip(r.item)}
      />
    );
  };

  const dotClass = (variant) => {
    if (variant === "happening") return "bg-tb-gold-bright shadow-[0_0_8px_2px_rgba(230,184,74,0.7)]";
    if (variant === "upcoming") return "bg-transparent border-2 border-tb-navy/40";
    return "bg-tb-gold-bright";
  };

  return (
    <div data-testid="chapter-story-timeline" className="relative">
      {/* Phase 2 polish item 4: ONE continuous gold hairline for the
          entire timeline (all three sections). Prior build restarted
          the hairline per <TimelineSection>, creating visible gaps
          across the divider rows. Now a single absolute span covers
          the full length of the outer container. */}
      <span
        aria-hidden="true"
        className="absolute top-2 bottom-2 left-8 md:left-10 w-px bg-tb-gold/40"
      />
      <div className="space-y-6">
        {happeningNow.length > 0 && (
          <TimelineSection title="Happening Now" testId="section-happening-now">
            {happeningNow.map((r) => (
              <TimelineItem key={`${r.kind}-${r.item.id}`} dotClass={dotClass("happening")}>
                {renderCard(r, { variant: "happening" })}
              </TimelineItem>
            ))}
          </TimelineSection>
        )}
        {upcoming.length > 0 && (
          <TimelineSection title="Upcoming" testId="section-upcoming">
            {upcoming.map((r) => (
              <TimelineItem key={`${r.kind}-${r.item.id}`} dotClass={dotClass("upcoming")}>
                {renderCard(r, { variant: "upcoming" })}
              </TimelineItem>
            ))}
          </TimelineSection>
        )}
        {past.length > 0 && (
          <TimelineSection title="Past" testId="section-past">
            {past.map((r) => (
              <TimelineItem key={`${r.kind}-${r.item.id}`} dotClass={dotClass("past")}>
                {renderCard(r, { variant: "past" })}
              </TimelineItem>
            ))}
          </TimelineSection>
        )}
      </div>
    </div>
  );
}

/**
 * Group divider: small serif gold title flanked by two hairlines.
 * Only rendered when the group has content (caller responsibility).
 * The section no longer draws its own vertical rail — the parent
 * <StoryTimeline> now owns ONE continuous rail so the line reads
 * unbroken across dividers.
 */
function TimelineSection({ title, testId, children }) {
  return (
    <section data-testid={testId}>
      <div className="flex items-center gap-3 my-2 pl-12 md:pl-14" aria-hidden="true">
        <span className="flex-1 h-px bg-tb-gold/40" />
        <span className="font-serif-it italic text-tb-gold-bright text-sm md:text-base tracking-wider">
          — {title} —
        </span>
        <span className="flex-1 h-px bg-tb-gold/40" />
      </div>
      <ol className="relative space-y-4 md:space-y-5 pl-12 md:pl-14">
        {children}
      </ol>
    </section>
  );
}

function TimelineItem({ dotClass, children }) {
  return (
    <li className="relative">
      <span
        aria-hidden="true"
        className={"absolute -left-[18px] md:-left-[22px] top-6 w-2.5 h-2.5 rounded-full ring-2 ring-tb-cream " + dotClass}
      />
      {children}
    </li>
  );
}

/**
 * Single story card in the timeline. Left: square cover image.
 * Right: gold date range, serif title, description, ⋯ menu.
 * Empty highlights strip container is rendered but hidden when
 * `favorite_moments` is empty — Phase 2 will populate it.
 */
function TimelineStoryCard({ s, onOpen, onEdit, onDelete, onMove }) {
  const promoted = s.source === "booked_trip" || !!s.auto_promoted_from_trip_id;
  const dateRange = fmtDateRange(s.start_date, s.end_date);
  // 2026-07-18 Fix 2 — cover resolution order:
  //   1. `cover_photo_id` → lookup in `photos[]` for that URL
  //   2. `photos[0]`
  //   3. legacy `cover_image_url` / `image`
  //   4. Google Places `photo_names[0]` → resolved live below
  // Fifth fallback (no-photo placeholder) is handled in the JSX.
  const photos = Array.isArray(s.photos) ? s.photos : [];
  const coverById = s.cover_photo_id ? photos.find((p) => p === s.cover_photo_id || p?.id === s.cover_photo_id) : null;
  const coverUrl = (typeof coverById === "string" ? coverById : coverById?.url)
    || photos[0]
    || s.cover_image_url
    || s.image
    || "";
  const gpName = Array.isArray(s.photo_names) && s.photo_names[0] ? s.photo_names[0] : null;
  const hasCover = !!(coverUrl || gpName);
  const description =
    (s.favorite_moments || []).find((m) => (m || "").trim()) ||
    ((s.journal_entries || [])[0]?.text || "").trim() ||
    "";
  // Phase 3: prefer the new structured `highlights[]` array (icon + label),
  // fall back to the legacy `favorite_moments` string array so cards from
  // pre-Phase-3 stories still render at least a label chip.
  const structuredHighlights = (s.highlights || []).filter((h) => (h?.label || "").trim());
  const legacyHighlights = (s.favorite_moments || []).filter((m) => (m || "").trim());

  return (
    <div
      data-testid={TIDS.story(s.id)}
      className={`rounded-2xl bg-white border-2 shadow-sm overflow-hidden ${
        s.needs_highlights ? "border-tb-gold shadow-md ring-2 ring-tb-gold/30" : "border-tb-cream-3"
      }`}
    >
      {s.needs_highlights && (
        <button
          onClick={onEdit}
          data-testid={`story-needs-highlights-${s.id}`}
          className="w-full flex items-center justify-between gap-2 bg-tb-gold/20 border-b-2 border-tb-gold px-3 py-2 text-left tb-tap hover:bg-tb-gold/30"
        >
          <span className="font-hand text-tb-navy text-sm flex items-center gap-1.5">
            <Star className="w-4 h-4 text-tb-gold fill-tb-gold" />
            welcome back from {s.trip_name}! add a few highlights →
          </span>
          <ChevronRight className="w-4 h-4 text-tb-navy/60" />
        </button>
      )}
      <div className="flex gap-3 p-3">
        <button
          type="button"
          onClick={onOpen}
          data-testid={`chapter-story-open-${s.id}`}
          className="flex-shrink-0 w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden bg-white border border-tb-navy/10 tb-tap"
          aria-label={hasCover ? `Open story: ${s.trip_name}` : `Add a photo to ${s.trip_name}`}
        >
          {coverUrl ? (
            <img
              src={coverUrl} alt="" loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            // Phase 5 (2026-07-28): TravelBio-branded placeholder.
            // Previously fell through to a live Google Places photo;
            // now shows the app-wide "Add cover photo" navy tile.
            <MissingCoverPlaceholder size="sm" />
          )}
        </button>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              {dateRange && (
                <div
                  data-testid={`chapter-story-daterange-${s.id}`}
                  className="font-hand text-tb-gold-bright text-[11px] tracking-wider uppercase"
                >
                  {dateRange}
                </div>
              )}
              <button
                type="button"
                onClick={onOpen}
                className="text-left w-full tb-tap"
              >
                <div className="font-serif-it italic text-tb-navy text-lg md:text-xl leading-tight truncate">
                  {s.trip_name}
                </div>
              </button>
              {promoted && (
                <div className="mt-1 flex">
                  <span
                    data-testid={`chapter-story-booked-badge-${s.id}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-tb-navy-deep border border-tb-navy-deep text-tb-cream font-hand text-[10px] tracking-wide"
                  >
                    <Star className="w-2.5 h-2.5 text-tb-gold-bright" fill="currentColor" strokeWidth={0} />
                    booked with TravelBio
                  </span>
                </div>
              )}
            </div>
            {/* 2026-07-17 — RowMenu removed from timeline cards.
                Users tap the card to open the unified detail page,
                where the ⋯ menu now hosts every action (Edit / Share /
                Move / Promote / Delete). Keeps the timeline calmer and
                removes a redundant hit target. */}
          </div>
          {description && (
            <p
              data-testid={`chapter-story-preview-${s.id}`}
              className="mt-1 font-ui text-sm text-tb-navy/85 line-clamp-3"
            >
              {description}
            </p>
          )}
          {/* Phase 3 hook: highlights preview strip. Prefer new
              structured highlights (icon + label) — max 3 chips —
              and fall back to legacy `favorite_moments` strings so
              pre-Phase-3 stories still show something. Upcoming
              trips explicitly do NOT render this (handled by the
              separate <TimelineBookedTripCard variant="upcoming">). */}
          {structuredHighlights.length > 0 ? (
            <div
              data-testid={`chapter-story-highlights-${s.id}`}
              className="mt-2 flex flex-wrap gap-1.5"
            >
              {structuredHighlights.slice(0, 3).map((h) => (
                <span
                  key={h.id || `${s.id}-${h.label}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-tb-navy/15 font-hand text-[10px] text-tb-navy/75 max-w-[10rem] truncate"
                >
                  <HighlightIconGlyph slug={h.icon} className="w-3 h-3 text-tb-gold-bright shrink-0" />
                  <span className="truncate">{h.label}</span>
                </span>
              ))}
            </div>
          ) : legacyHighlights.length > 0 ? (
            <div
              data-testid={`chapter-story-highlights-${s.id}`}
              className="mt-2 flex flex-wrap gap-1.5"
            >
              {legacyHighlights.slice(0, 3).map((m, i) => (
                <span
                  key={`${s.id}-hl-${i}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-tb-navy/15 font-hand text-[10px] text-tb-navy/75 max-w-[10rem] truncate"
                >
                  <Sparkles className="w-2.5 h-2.5 text-tb-gold-bright shrink-0" />
                  <span className="truncate">{m}</span>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Booked trip that doesn't yet have a promoted-story row. Rendered
 * inside the same timeline so the year reads chronologically without
 * a separate "Stories created through TravelBio" heading (that split
 * lived on the pre-redesign page and gave duplicate feelings even
 * after #36 dedup).
 */
function TimelineBookedTripCard({ t, variant = "past", onOpen, onEdit, onDelete, onMove }) {
  const nav = useNavigate();
  const dateRange = fmtDateRange(t.start_date, t.end_date);
  const isHappening = variant === "happening";
  const isUpcoming = variant === "upcoming";
  // 2026-07-19 — "Add travel insurance?" pill on upcoming trip
  // cards only. Fires the analytics endpoint (fire-and-forget) then
  // navigates to `/marketplace/travel-insurance` where the Allianz
  // AgentMax iframe lives.
  const onAddInsurance = (e) => {
    e.stopPropagation();
    api.post("/insurance-clicks/outbound", {
      source: "booked_trip_upcoming",
      trip_id: t.id,
    // eslint-disable-next-line no-console
    }).catch((err) => console.error("[insurance] booked_trip_upcoming ping failed", err));
    nav("/marketplace/travel-insurance");
  };
  // 2026-07-18 Fix 2 — booked trips also carry `photos[]` now (added
  // in Phase 3). Mirror the story cover-resolution order so uploaded
  // trip photos surface on the timeline too.
  const tPhotos = Array.isArray(t.photos) ? t.photos : [];
  const tCoverById = t.cover_photo_id ? tPhotos.find((p) => p === t.cover_photo_id || p?.id === t.cover_photo_id) : null;
  const tCoverUrl = (typeof tCoverById === "string" ? tCoverById : tCoverById?.url)
    || tPhotos[0]
    || t.cover_image_url
    || t.image
    || "";
  const dateAccent =
    isHappening ? "text-tb-gold-bright"
    : isUpcoming ? "text-tb-navy/50"
    : "text-tb-blue";
  const borderClass =
    isHappening ? "border-tb-gold ring-2 ring-tb-gold/30 shadow-lg"
    : isUpcoming ? "border-tb-navy/20 opacity-90"
    : "border-tb-blue/30";
  return (
    <div
      data-testid={TIDS.trip(t.id)}
      className={`rounded-2xl bg-white border-2 shadow-sm p-3 flex items-start gap-3 ${borderClass}`}
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid={`chapter-trip-open-${t.id}`}
        aria-label={tCoverUrl ? `Open trip: ${t.name || t.destination_name}` : `Open ${t.name || t.destination_name}`}
        className={
          "flex-shrink-0 w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden flex items-center justify-center tb-tap " +
          (isUpcoming ? "bg-tb-navy/5 border border-tb-navy/10" : "bg-tb-blue-soft")
        }
        style={isUpcoming ? { opacity: 0.85 } : {}}
      >
        {tCoverUrl ? (
          <img src={tCoverUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <Luggage className={"w-9 h-9 " + (isUpcoming ? "text-tb-navy/50" : "text-tb-blue")} strokeWidth={1.4} />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {/* State pill above title — celebratory for Happening,
                muted outline for Upcoming, nothing for Past (past
                trips already show the affiliate pill inside the
                card when applicable). */}
            {isHappening && (
              <span
                data-testid={`chapter-trip-happening-pill-${t.id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-tb-gold text-tb-navy-deep font-hand text-[10px] tracking-wide mb-1"
              >
                <Sparkles className="w-2.5 h-2.5" /> Happening now
              </span>
            )}
            {isUpcoming && (
              <span
                data-testid={`chapter-trip-upcoming-pill-${t.id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-tb-navy/40 text-tb-navy/60 font-hand text-[10px] tracking-wide mb-1"
              >
                Upcoming
              </span>
            )}
            {dateRange && (
              <div className={`font-hand text-[11px] tracking-wider uppercase ${dateAccent}`}>
                {dateRange}
              </div>
            )}
            <button type="button" onClick={onOpen} className="text-left tb-tap">
              <div className={
                "font-serif-it italic text-lg md:text-xl leading-tight truncate " +
                (isUpcoming ? "text-tb-navy/70" : "text-tb-navy")
              }>
                {t.name}
              </div>
            </button>
            <p className="font-hand text-tb-navy/65 text-xs flex items-center gap-1 truncate">
              <MapPin className="w-3 h-3" />
              {[t.destination_name, t.country].filter(Boolean).join(", ") || "—"}
            </p>
            {isUpcoming && (
              <button
                type="button"
                onClick={onAddInsurance}
                data-testid={`chapter-card-travel-insurance-${t.id}`}
                className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-tb-navy/25 bg-white px-2.5 py-1 font-hand text-xs text-tb-navy/70 hover:text-tb-navy hover:border-tb-navy/50 hover:bg-tb-navy/5 transition tb-tap"
              >
                <Shield className="w-3 h-3" strokeWidth={2} />
                Add travel insurance?
              </button>
            )}
          </div>
          {/* 2026-07-17 — RowMenu removed. Tap the card to open the
              unified detail page; ⋯ actions live there. */}
        </div>
      </div>
    </div>
  );
}

/**
 * Empty state block for a year with 0 stories & 0 booked trips.
 * Deliberately centered inside the book page so the layout still
 * reads as a book — the CTA to write lives in the footer, not here.
 *
 * 2026-07-17: soft CTA copy is now year-context-aware.
 *   • past year    → "Add stories from past adventures"
 *   • current year → "Add past or upcoming stories to this year's chapter"
 *   • future year  → "Start building your future memories here"
 * The soft CTA click still opens the past-story flow for past/current
 * years; for future years it opens the booked-trip flow instead,
 * since adding a "past story" for a year that hasn't happened is
 * nonsensical.
 */
function EmptyChapterState({ year, onAddPast, onAddBookedTrip }) {
  const currentYear = new Date().getFullYear();
  const isPast = year < currentYear;
  const isCurrent = year === currentYear;
  const isFuture = year > currentYear;

  const softCta = isFuture
    ? "Start building your future memories here"
    : isCurrent
    ? "Add past or upcoming stories to this year's chapter"
    : "Add stories from past adventures";
  const onSoftCtaClick = isFuture ? onAddBookedTrip : onAddPast;

  return (
    <div
      data-testid="chapter-empty-state"
      data-year-context={isPast ? "past" : isCurrent ? "current" : "future"}
      className="text-center py-12 md:py-16"
    >
      <div className="mx-auto w-12 h-12 rounded-full bg-tb-navy-deep flex items-center justify-center">
        <BookOpen className="w-6 h-6 text-tb-gold-bright" strokeWidth={1.6} />
      </div>
      <p className="mt-3 font-serif-it italic text-tb-navy text-2xl">
        No stories in {year} yet
      </p>
      <button
        type="button"
        onClick={onSoftCtaClick}
        data-testid="chapter-empty-add-past"
        className="mt-2 inline-flex items-center gap-1 font-hand text-tb-gold-bright hover:text-tb-gold text-sm underline underline-offset-4 tb-tap"
      >
        {softCta}
      </button>
    </div>
  );
}

/**
 * Right-edge vertical rail of book-edge year tabs. Fetches the
 * merged year set (default 5 + user's pinned extras) from
 * `GET /api/user/chapter-years`. The "+" button below the last tab
 * opens a small popover for adding an older year.
 *
 * The rail is `fixed` to the right edge with a 16-24px buffer so on
 * desktop it doesn't touch the viewport edge — matches the spec's
 * "add ~16-24px horizontal buffer between the year-tab rail and the
 * right edge of the viewport".
 */
function YearRail({ selectedYear, onSelect }) {
  const [defaultYears, setDefaultYears] = useState([]);
  const [extraYears, setExtraYears] = useState([]);
  const [picking, setPicking] = useState(false);
  const scrollRef = useRef(null);
  const activeRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/user/chapter-years");
      setDefaultYears(data.default_years || []);
      setExtraYears(data.extra_years || []);
    } catch { /* silent — rail falls back to computed defaults */ }
  }, []);

  // 2026-07-17 Fix 1 — refetch on every year change AND on window
  // focus so extras added on other devices/tabs (e.g. mobile app auto-
  // pinning a booked-trip year) surface on the next render. Previously
  // the effect ran only once on mount; navigating between years kept
  // the same YearRail instance with stale state.
  useEffect(() => { load(); }, [load, selectedYear]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const merged = [...defaultYears, ...extraYears];
  if (selectedYear && !merged.includes(selectedYear)) merged.push(selectedYear);
  merged.sort((a, b) => b - a);

  // 2026-07-17 Fix 1 — auto-scroll the selected tab into view once
  // the rail renders (or when navigation changes the selection). We
  // use `scrollIntoView({ block: "center" })` so on tall lists the
  // selected tab is visually centered instead of clipped at the top
  // or bottom edge of the scroll column.
  useEffect(() => {
    const el = activeRef.current;
    if (!el) return;
    // Deferred to next tick so the DOM has settled after the merge
    // computes and the tab elements paint.
    const id = requestAnimationFrame(() => {
      try { el.scrollIntoView({ block: "center", behavior: "auto" }); }
      catch { /* older browsers — ignore */ }
    });
    return () => cancelAnimationFrame(id);
  }, [selectedYear, merged.length]);

  const addYear = async (y) => {
    try {
      const { data } = await api.patch("/user/chapter-years", { add: y });
      setDefaultYears(data.default_years || []);
      setExtraYears(data.extra_years || []);
      setPicking(false);
      onSelect(y);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add year");
    }
  };

  return (
    <>
      {/* Phase 2 polish A1 + 2026-07-16 rail-fix pass:
          - `top-32 md:top-36` = 128 / 144px keeps the rail well clear
            of the AppShell header (which is 96px tall). Gives ~30-50px
            breathing room between the header bottom edge and the first
            tab's top edge.
          - `z-10` is BELOW the header's implicit stacking context, so
            even if a viewport height edge case caused geometry to
            overlap, header icons (hamburger / logo / avatar / search
            / bell / chat / analytics / logout) always win the click.
            Chose lowering the rail rather than bumping the header to
            z-40 because the header is `position:relative` (part of the
            document flow) and touching its z-index risks side effects
            on the drawer / modal stack.
          - `right-1 md:right-3` (shifted LEFT from `right-4 md:right-6`)
            so the tab's flat left edge overlaps into the book's
            page-depth strips by ~12-16px, matching the reference
            image's "tucked between pages" look. */}
      {/* Year rail — rewritten 2026-07-17 (third pass) after two
          overcorrections. User feedback: the previous `min(100dvh,
          100vh)` cap + 140px padding forced the flex layout to squeeze
          tabs and truncate the last one mid-body. The user does NOT
          care if Safari's floating toolbar visually hovers over the
          bottom tab — they care only that every tab is a FULL
          rectangle. So:
            • Fixed positioning `top-32 bottom-4` (md `top-36 bottom-6`)
              — take the natural viewport-minus-header space with a
              tiny 4-6px bottom gap. No dvh math, no safe-area padding.
            • Each tab is `h-[140px] shrink-0` — never flex-shrinks
              below its natural rectangle. If N tabs × 140px + gaps
              exceeds the rail, the whole column scrolls internally.
            • `scroll-snap-y: mandatory` on the column + `scroll-snap-
              align: start` on each tab so the scroll always lands on
              a whole tab boundary — never mid-body.
            • On mobile Safari the browser's floating toolbar may
              visually hover over the bottom tab. Acceptable: the tab
              is fully rendered underneath.
      */}
      <div
        data-testid="chapter-year-rail"
        className="fixed top-32 md:top-36 bottom-4 md:bottom-6 right-1 md:right-3 z-10 flex flex-col items-end pointer-events-none"
      >
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 w-auto flex flex-col items-end gap-1.5 md:gap-2 overflow-y-auto pointer-events-auto py-1"
          style={{
            scrollSnapType: "y mandatory",
            scrollbarWidth: "none",
          }}
        >
          {merged.map((y) => {
            const active = y === selectedYear;
            return (
              <button
                key={y}
                ref={active ? activeRef : null}
                type="button"
                data-testid={`chapter-year-tab-${y}`}
                onClick={() => onSelect(y)}
                aria-pressed={active}
                style={{ scrollSnapAlign: "start" }}
                className={
                  // 2026-07-17 rail rewrite:
                  //   • `h-[140px] shrink-0` — every tab is a full
                  //     140px rectangle, no flex-shrink truncation.
                  //   • `scroll-snap-align: start` (via inline style)
                  //     so overflow scroll lands on whole-tab
                  //     boundaries only.
                  "pointer-events-auto relative h-[140px] shrink-0 w-[56px] md:w-[64px] rounded-l-none rounded-r-2xl border-2 border-l-0 " +
                  "flex flex-col items-center justify-center transition-all tb-tap shadow-md " +
                  (active
                    ? "bg-tb-navy-deep border-tb-gold text-tb-gold-bright translate-x-0 md:translate-x-1 scale-105 z-[2]"
                    : "bg-tb-navy border-tb-navy-deep text-tb-gold/80 hover:bg-tb-navy-deep z-[1]")
                }
              >
                <span
                  aria-hidden="true"
                  className={
                    "absolute inset-y-1.5 right-1.5 left-0 rounded-r-xl border-t border-r border-b pointer-events-none " +
                    (active ? "border-tb-gold-bright/70" : "border-tb-gold/35")
                  }
                />
                {/* Deeper "tucked into the page-depth" faux fan — 4
                    cream strips in front of the tab's flat left edge,
                    progressively LIGHTER / more FORWARD as they go
                    outward (leftward). Each 3px wide + 2px offset =
                    ~12-14px total visible depth. Darkest strip nests
                    closest to the tab body (most recessed); lightest
                    strip is the outermost visible page. */}
                <span aria-hidden="true" className="absolute -left-[3px]  top-1   bottom-1  w-[3px] bg-tb-cream-3    rounded-l pointer-events-none" />
                <span aria-hidden="true" className="absolute -left-[6px]  top-2   bottom-2  w-[3px] bg-tb-cream-2    rounded-l pointer-events-none" />
                <span aria-hidden="true" className="absolute -left-[9px]  top-3   bottom-3  w-[3px] bg-tb-cream/95   rounded-l pointer-events-none" />
                <span aria-hidden="true" className="absolute -left-[12px] top-4   bottom-4  w-[3px] bg-tb-cream/85   rounded-l pointer-events-none" />
                <span className="relative font-serif-it italic text-lg md:text-xl leading-none">
                  {y}
                </span>
                <CompassRoseSvg className={"relative mt-2 w-4 h-4 md:w-4 md:h-4 " + (active ? "text-tb-gold-bright" : "text-tb-gold/60")} />
              </button>
            );
          })}
          <button
            type="button"
            data-testid="chapter-year-add-btn"
            aria-label="Add another year"
            onClick={() => setPicking(true)}
            className="pointer-events-auto shrink-0 self-center mt-1 w-10 h-10 md:w-11 md:h-11 rounded-full bg-tb-navy-deep text-tb-gold-bright flex items-center justify-center border-2 border-tb-gold/60 hover:brightness-110 tb-tap shadow-md"
          >
            <Plus className="w-5 h-5" strokeWidth={2.4} />
          </button>
        </div>
      </div>

      {picking && (
        <AddYearDialog
          existingYears={merged}
          onClose={() => setPicking(false)}
          onPick={addYear}
        />
      )}
    </>
  );
}

/**
 * Modal for adding an older chapter year to the right-rail. Simple
 * scrollable list of years from currentYear down to currentYear-50,
 * with the ones already pinned rendered as disabled/checked so the
 * user sees them but can't dupe-add.
 */
function AddYearDialog({ existingYears, onClose, onPick }) {
  const existingSet = new Set(existingYears);
  const years = [];
  // Phase 2 merge B4: range now spans currentYear+10 (future planning)
  // down to currentYear-50 (retrospective chapters). Future years
  // appear at the top so the picker feels forward-leaning.
  for (let y = CURRENT_YEAR + 10; y >= CURRENT_YEAR - 50; y--) years.push(y);
  return (
    <div className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="chapter-year-picker-dialog"
        onClick={(e) => e.stopPropagation()}
        className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-sm w-full max-h-[70vh] flex flex-col"
      >
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span className="font-hand text-lg">Add a chapter year</span>
          <button onClick={onClose} className="font-hand text-tb-gold tb-tap">close</button>
        </div>
        <ul className="p-2 space-y-1 overflow-y-auto">
          {years.map((y) => {
            const disabled = existingSet.has(y);
            return (
              <li key={y}>
                <button
                  type="button"
                  disabled={disabled}
                  data-testid={`chapter-year-picker-${y}`}
                  onClick={() => onPick(y)}
                  className={
                    "w-full flex items-center justify-between px-3 py-2 rounded-lg font-serif-it italic text-lg tb-tap " +
                    (disabled
                      ? "text-tb-navy/40 cursor-not-allowed"
                      : "text-tb-navy hover:bg-white")
                  }
                >
                  <span>Chapter {y}</span>
                  {disabled && <span className="font-hand text-xs">already pinned</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/**
 * "JAN 12 – 16" style compact date range. Same-month gets one month
 * label; cross-month prints both. Empty when start_date is missing.
 */
function fmtDateRange(startIso, endIso) {
  if (!startIso) return "";
  const d1 = new Date(startIso);
  if (isNaN(d1)) return "";
  const m1 = d1.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
  const day1 = d1.getDate();
  if (!endIso) return `${m1} ${day1}`;
  const d2 = new Date(endIso);
  if (isNaN(d2)) return `${m1} ${day1}`;
  const day2 = d2.getDate();
  const m2 = d2.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
  if (m1 === m2) return `${m1} ${day1} – ${day2}`;
  return `${m1} ${day1} – ${m2} ${day2}`;
}


/**
 * Phase 2 merge B3: pending-confirmation pills.
 *
 * Fetches all booked-trip rows for the caller and filters to the
 * `state == "awaiting_confirmation"` subset — those are the trips
 * created by the affiliate-click layer that still need the user's
 * "Yes, I booked this" confirmation before we tag them with the
 * `booked_via_travelbio` badge and start attributing analytics.
 *
 * Yes → POST /booked-trips/{id}/confirm (existing endpoint).
 * No  → DELETE /booked-trips/{id} (existing endpoint) — TTL would
 *       purge them at 72 h anyway, but manual dismissal is faster.
 *
 * Stack shows up to 3 pills; the rest collapse into a "+ N more"
 * expand button so a user with a click-happy afternoon doesn't
 * drown the hero.
 */
function PendingConfirmationBar({ onChange }) {
  const [pending, setPending] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/booked-trips");
      const rows = (data?.trips || []).filter((t) => t.state === "awaiting_confirmation");
      setPending(rows);
    } catch {
      /* silent — an empty bar is the correct fallback */
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (pending.length === 0) return null;

  const decide = async (t, yes) => {
    setBusyId(t.id);
    try {
      if (yes) await api.post(`/booked-trips/${t.id}/confirm`);
      else await api.delete(`/booked-trips/${t.id}`);
      toast.success(yes ? "Booking confirmed" : "Dismissed");
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't update");
    } finally {
      setBusyId(null);
    }
  };

  const visible = expanded ? pending : pending.slice(0, 3);
  const remaining = pending.length - visible.length;

  return (
    <div
      data-testid="chapter-pending-confirmation-bar"
      className="mb-4 space-y-2"
    >
      {visible.map((t) => {
        const hoursLeft = (() => {
          if (!t.awaiting_since) return 72;
          const started = new Date(t.awaiting_since).getTime();
          if (isNaN(started)) return 72;
          const elapsedH = (Date.now() - started) / (1000 * 60 * 60);
          return Math.max(0, Math.round(72 - elapsedH));
        })();
        return (
          <div
            key={t.id}
            data-testid={`chapter-pending-confirm-${t.id}`}
            className="rounded-xl border border-tb-navy/15 bg-white px-3 py-2 flex items-center gap-3 shadow-[0_2px_10px_-4px_rgba(26,58,95,0.15)]"
          >
            <Sparkles className="w-4 h-4 text-tb-gold-bright shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-serif-it italic text-tb-navy text-sm truncate">
                {t.name || t.destination_name || "Your booking"}
              </div>
              <div className="font-hand text-tb-navy/70 text-xs">
                Did you book this?{" "}
                <span className="text-tb-navy/45">
                  {hoursLeft}h left
                </span>
              </div>
            </div>
            <button
              type="button"
              data-testid={`chapter-pending-yes-${t.id}`}
              disabled={busyId === t.id}
              onClick={() => decide(t, true)}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-tb-navy text-white font-hand text-xs border-2 border-tb-navy hover:bg-tb-navy-deep tb-tap disabled:opacity-60 transition-colors"
            >
              <Check className="w-3 h-3" /> Yes
            </button>
            <button
              type="button"
              data-testid={`chapter-pending-no-${t.id}`}
              disabled={busyId === t.id}
              onClick={() => decide(t, false)}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-tb-navy/70 font-hand text-xs border-2 border-tb-navy/30 tb-tap hover:border-red-400 hover:text-red-600 disabled:opacity-60 transition-colors"
            >
              <XIcon className="w-3 h-3" /> No
            </button>
          </div>
        );
      })}
      {remaining > 0 && !expanded && (
        <button
          type="button"
          data-testid="chapter-pending-expand"
          onClick={() => setExpanded(true)}
          className="w-full py-1 rounded-lg font-hand text-xs text-tb-navy/60 hover:text-tb-gold-bright tb-tap"
        >
          + {remaining} more pending
        </button>
      )}
    </div>
  );
}


/**
 *
 * The card on `/chapters/{year}` now only shows a summary tile (cover
 * photo, title, badge, 1-line preview) — the rich content the previous
 * inline layout had (photo carousel, all highlights, journal entries,
 * backpack, expenses, itinerary chips) moves here. Opens on tap.
 *
 * Auto-promoted stories additionally render the linked booked trip's
 * data (expenses, itinerary counters, journal entries mirrored from
 * the trip) via <PromotedTripInline>. Manually-composed stories only
 * render their own `journal_entries` array (no linked trip payload).
 */
function StoryDetailModal({ story, onClose }) {
  const s = story;
  const promoted = s.source === "booked_trip" || !!s.auto_promoted_from_trip_id;
  // Close on ESC — standard modal keyboard affordance.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-start md:items-center justify-center p-0 md:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid={`story-detail-modal-${s.id}`}
    >
      <div
        className="bg-tb-cream rounded-t-2xl md:rounded-2xl border-2 border-tb-cream-3 w-full md:max-w-2xl my-0 md:my-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-tb-navy-deep text-tb-cream px-4 py-3 flex items-center justify-between md:rounded-t-2xl sticky top-0 z-10">
          <span className="font-hand text-lg truncate">{s.trip_name}</span>
          <button
            type="button"
            onClick={onClose}
            data-testid="story-detail-close"
            className="font-hand text-tb-gold tb-tap"
          >
            close
          </button>
        </div>
        {(s.photos && s.photos.length > 0) ? (
          <PhotoCarousel photos={s.photos} testId={`story-detail-carousel-${s.id}`} />
        ) : s.image && (
          <img src={s.image} alt="" className="w-full h-56 object-cover" />
        )}
        <div className="p-4 space-y-3">
          {promoted && (
            <div className="flex">
              <span
                data-testid={`story-detail-booked-badge-${s.id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-tb-navy-deep border border-tb-navy-deep text-tb-cream font-hand text-[11px] tracking-wide"
              >
                <Star className="w-2.5 h-2.5 text-tb-gold-bright" fill="currentColor" strokeWidth={0} />
                booked with TravelBio
              </span>
            </div>
          )}
          <div>
            <h2 className="font-serif-it text-tb-navy text-2xl">{s.trip_name}</h2>
            <p className="font-hand text-tb-navy/65 text-sm flex items-center gap-1 mt-1">
              <MapPin className="w-3.5 h-3.5" /> {s.country}
              <CalendarDays className="w-3.5 h-3.5 ml-2" />
              {fmt(s.start_date)}{s.end_date && ` → ${fmt(s.end_date)}`}
            </p>
          </div>
          {(s.favorite_moments || []).filter((m) => (m || "").trim()).length > 0 && (
            <div>
              <h3 className="font-serif-it text-tb-navy text-base mb-1.5">Highlights</h3>
              <ul className="space-y-1">
                {s.favorite_moments.filter((m) => (m || "").trim()).map((m, i) => (
                  <li key={`${s.id}-detail-m-${i}`} className="flex items-start gap-2 font-ui text-sm text-tb-navy/85">
                    <span className="font-serif-it text-tb-coral text-base leading-none mt-0.5">{i + 1}.</span>
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {s.auto_promoted_from_trip_id ? (
            <PromotedTripInline story={s} tripId={s.auto_promoted_from_trip_id} />
          ) : (
            (s.journal_entries || []).length > 0 && (
              <StoryJournalTab entries={s.journal_entries} storyId={s.id} />
            )
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Item #18 — inline story body for auto-promoted booked trips.
 *
 * Renders the trip's journal entries as flowing body paragraphs
 * (no click-to-expand — this IS the story text), an expense summary
 * chip strip (total spent, expense count) and inlines the trip's
 * backpack/shopping list. Fetches the trip document once on mount so
 * `expenses` and `itinerary` counters are accurate.
 *
 * Fall-through: when the trip fetch fails or returns nothing (e.g.
 * user later deleted the trip after promotion), we quietly render
 * only the story's own `journal_entries` array so we never regress
 * to a blank story body.
 */
function PromotedTripInline({ story, tripId }) {
  const [trip, setTrip] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/booked-trips/${encodeURIComponent(tripId)}`);
        if (!cancelled) setTrip(data);
      } catch { /* trip may have been deleted post-promotion — silent */ }
    })();
    return () => { cancelled = true; };
  }, [tripId]);

  const journal = story.journal_entries || [];
  const expenses = trip?.expenses || [];
  const spent = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const currency = expenses[0]?.currency || trip?.currency || "USD";
  const itinCount = (trip?.itinerary || []).length;

  return (
    <div className="mt-3 pt-3 border-t border-dashed border-tb-navy/15 space-y-3">
      {(spent > 0 || itinCount > 0 || journal.length > 0) && (
        <div
          data-testid={`chapter-promoted-summary-${story.id}`}
          className="flex flex-wrap items-center gap-2"
        >
          {journal.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-tb-cream-2 border border-tb-cream-3 font-hand text-[11px] text-tb-navy/70">
              <BookOpen className="w-3 h-3 text-tb-gold-bright" />
              {journal.length} {journal.length === 1 ? "day" : "days"} journaled
            </span>
          )}
          {itinCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-tb-cream-2 border border-tb-cream-3 font-hand text-[11px] text-tb-navy/70">
              <MapPin className="w-3 h-3 text-tb-coral" />
              {itinCount} plans
            </span>
          )}
          {spent > 0 && (
            <span
              data-testid={`chapter-promoted-expense-${story.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-tb-blue-soft border border-tb-blue/40 font-hand text-[11px] text-tb-navy"
            >
              spent {currency} ${Math.round(spent).toLocaleString()}
            </span>
          )}
        </div>
      )}

      {journal.length > 0 && (
        <ol
          data-testid={`chapter-promoted-journal-${story.id}`}
          className="space-y-2"
        >
          {journal.map((e, i) => (
            <li
              key={e.id || `${story.id}-je-${i}`}
              className="rounded-xl bg-tb-cream/60 border border-tb-cream-3 p-3"
            >
              {e.date && (
                <div className="font-hand text-tb-navy/60 text-[11px] mb-1">
                  {e.date}
                </div>
              )}
              <div className="font-ui text-sm text-tb-navy/90 whitespace-pre-wrap">
                {e.text}
              </div>
            </li>
          ))}
        </ol>
      )}

      <BackpackList tripId={tripId} mode="story" />
    </div>
  );
}


/**
 * Journal tab appended below a story's highlights preview. Collapsed by
 * default — a click opens the day-by-day journal entries auto-imported
 * from the booked trip (kept in sync via the trip journal endpoint).
 */
function StoryJournalTab({ entries, storyId }) {
  const [open, setOpen] = useState(false);
  const count = entries.length;
  return (
    <div className="mt-3 pt-3 border-t border-dashed border-tb-navy/15">
      <button
        type="button"
        data-testid={`story-journal-toggle-${storyId}`}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 font-hand text-sm text-tb-navy tb-tap"
      >
        <BookOpen className="w-4 h-4 text-tb-gold-bright" />
        {open ? "hide" : "open"} journal
        <span className="text-tb-navy/50">
          ({count} {count === 1 ? "entry" : "entries"})
        </span>
      </button>
      {open && (
        <ol className="mt-3 space-y-3">
          {entries.map((e, i) => (
            <li
              key={e.id || `${storyId}-je-${i}`}
              data-testid={`story-journal-entry-${storyId}-${i}`}
              className="rounded-xl bg-tb-cream/60 border border-tb-cream-3 p-3"
            >
              {e.date && (
                <div className="font-hand text-tb-navy/60 text-[11px] mb-1">
                  {e.date}
                </div>
              )}
              <div className="font-ui text-sm text-tb-navy/90 whitespace-pre-wrap">
                {e.text}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
